








































import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { MangoPayTransaction, Wallet, Transactions } from '@/store/wallet/types';
import { TransactionTypes, TransactionStatus } from '@/enum/mangoPay/TransactionTypes';
import { centsToEuro } from '@/filters/currency';
import { date } from '@/filters/date';
import { BankAccount } from '@/store/mangoPay/types';
import ExporoTable from '@/components/_common/Table.vue';
import ExporoTooltip from '@/components/_common/Tooltip.vue';

@Component({
  name: 'transactions-list',
  filters: {
    centsToEuro,
    date,
  },
  components: {
    ExporoTable,
    ExporoTooltip,
  },
})
export default class TransactionsOverviewCard extends Vue {
  @Prop({ default: undefined, required: true }) transactions?: Transactions;

  @Prop({ default: undefined, required: true }) isLoading?: Boolean;
  @Prop({ default: undefined, required: true }) wallet?: Wallet;

  @Getter('mangoPay/getBankAccountById') getBankAccountById?: Function;
  @Getter('mangoPay/getMandateById') getMandateById?: Function;

  private visibilityOff: boolean = true;

  get tableData(): Array<object> {
    const tableData: Array<object> = [];

    if (this.transactions && this.transactions.data) {
      this.transactions.data
        .map(transaction => {
          tableData.push({
            [ this.$t('transactionsList.dataTable.label', { context: 'description' }) ]: {
              text: this.setTransactionIcons(transaction),
              value: transaction.Type,
            },
            [ this.$t('transactionsList.dataTable.label', { context: 'amount' }) ]: {
              text: this.parseAmount(transaction.CreditedFunds.Amount),
              value: transaction.CreditedFunds.Amount,
            },
            [ this.$t('transactionsList.dataTable.label', { context: 'details' }) ]: {
              text: this.getDetails(transaction),
              value: this.getDetails(transaction),
            },
            [ this.$t('transactionsList.dataTable.label', { context: 'date' }) ]: {
              text: this.parseDate(transaction.CreationDate),
              value: transaction.CreationDate,
            },
            [ this.$t('transactionsList.dataTable.label', { context: 'status' }) ]: {
              text: this.setStatusIcon(transaction),
              value: transaction.Status,
            },
          });
        });
    }

    return tableData;
  }

  parseDate(date) {
    return `<label class="color-text-regular">${this.$options.filters.date(date)}</label>`;
  }

  parseAmount(amount) {
    return this.$options.filters.centsToEuro(amount);
  }

  setTransactionIcons(transaction: MangoPayTransaction) {
    let color = transaction.CreditedWalletId === this.wallet.Id ? 'success' : 'error';
    let icon = '';
    let title = '';

    if (transaction.CreditedWalletId === this.wallet.Id) {
      icon = 'trending_up';
      title = this.$t('transactionsList.label', { context: 'charge' });

    }
    if (transaction.Type === TransactionTypes.PAY_OUT) {
      icon = 'trending_down';
      title = this.$t('transactionsList.label', { context: 'withdraw' });
    }

    if (transaction.Type === TransactionTypes.TRANSFER) {
      color = 'primary';
      icon = 'sync_alt';
      title = this.$t('transactionsList.label', { context: 'sent' });

      if (transaction.CreditedWalletId === this.wallet.Id) {
        title = this.$t('transactionsList.label', { context: 'received' });
      }
    }
    const transactionType = this.$t(`mangoPay.types.${transaction.Type}`);

    return `<label class="color-text-regular flex-container row"><i title="${transactionType}" class="transactions-list__icon font-size-medium material-icons color-text-${color}">
              ${icon}
            </i> ${title}</label>`;
  }

  setStatusIcon(transaction: MangoPayTransaction) {
    let className, icon;
    let message = this.$t(`mangoPay.message.${transaction.ResultMessage}`);

    if (transaction.Status === TransactionStatus.SUCCEEDED) {
      className = 'color-text-success';
      icon = 'done';
    }

    if (transaction.Status === TransactionStatus.CREATED) {
      className = '';
      icon = 'update';
      message = this.$t(`mangoPay.types.${TransactionStatus.CREATED}`);
    }

    if (transaction.Status === TransactionStatus.FAILED) {
      className = 'color-text-error';
      icon = 'warning';
    }

    const transactionType = this.$t(`mangoPay.types.${transaction.Status}`);

    return `<label class="color-text-regular flex-container row"><i title="${transactionType}" class="transactions-list__icon font-size-medium material-icons ${className}" >
      ${icon}
    </i>${message}</label>`;
  }

  onTableUpdate(val) {
    this.$emit('update', val);
  }

  getProjectName(tag: string) {
    return tag && tag.split('|')[ 1 ] != undefined ? tag.split('|')[ 1 ] : '';
  }

  getBankAccountByMandate(mandateId: string) {
    const mandate = this.getMandateById(mandateId);

    return mandate ? this.getBankAccountIBAN(mandate.BankAccountId) : false;
  }

  getBankAccountIBAN(bankAccountId: string) {
    const bankAccount = this.getBankAccountById(bankAccountId);

    return bankAccount ? this.prepareDetails(bankAccount) : '';
  }

  getDetails(transaction: MangoPayTransaction) {
    switch (transaction.Type) {
      case TransactionTypes.PAY_IN: {
        if (transaction.MandateId) {
          return this.getBankAccountByMandate(transaction.MandateId);;
        }
        if (transaction.hasOwnProperty('BankAccount')) {
          const trans = Object.assign({IBAN: transaction.BankAccount.IBAN, Owners: [transaction.BankAccount.OwnerName]});

          return this.prepareDetails(trans);
        }

        return this.getBankAccountByMandate(transaction.MandateId);
      }
      case TransactionTypes.PAY_OUT: {

        return this.getBankAccountIBAN(transaction.BankAccountId);
      }
      case TransactionTypes.TRANSFER: {

        return this.getProjectName(transaction.Tag) || transaction.Tag;
      }
      default: {
        return this.getProjectName(transaction.Tag) || transaction.Tag;
      }
    }
  }

  /**
   * Shows the bank account name and
   * only four last characters of a IBAN number
   */
  private prepareDetails(account: BankAccount) {
    const { IBAN } = account;
    const censored = this.$t('forms.placeholder', { context: 'IBANCensored', number: `${IBAN.slice(IBAN.length - 4, IBAN.length)}` });

    let bankAccountName = '';
    let owner = '';
    if (account.title) {
      bankAccountName = `<h7 v-if="account.title">Bank: ${account.title}<br></h7>`;
    }
    // this information comes only with pay-ins made via wire transfer to wallet
    if (account.Owners) {
      owner = `<h7 v-if="account.title">Author: ${account.Owners[0]}<br></h7>`;
    }

    return `<a class="flex-container row" href="/bank-account/${account.Id}"><i class="material-icons">account_balance</i><label>${bankAccountName}${owner}${this.visibilityOff ? censored : IBAN}</label></a>`;
  }
}
